<template>
  <div class="login-page">
    <b-row class="m-0 min-vh-100">
      <!-- Left Side - Branding -->
      <b-col lg="7" class="d-none d-lg-flex branding-side">
        <div class="branding-content">
          <div class="branding-header">
            <img
              :src="brandLogo"
              :alt="brandName || 'SalesFlow'"
              class="brand-logo-img"
              :class="{ 'brand-logo-custom': hasCustomLogo }"
            />
            <h3 v-if="brandName" class="brand-name">{{ brandName }}</h3>
          </div>

          <div class="branding-features">
            <div class="feature-item">
              <div class="feature-icon">
                <feather-icon icon="ShoppingBagIcon" size="20" />
              </div>
              <div class="feature-text">
                <h4>Catálogo Completo</h4>
                <p>Acesse todo o seu catálogo de produtos em um só lugar</p>
              </div>
            </div>
            <div class="feature-item">
              <div class="feature-icon">
                <feather-icon icon="TruckIcon" size="20" />
              </div>
              <div class="feature-text">
                <h4>Pedidos Simplificados</h4>
                <p>Faça pedidos de forma rápida e acompanhe em tempo real</p>
              </div>
            </div>
            <div class="feature-item">
              <div class="feature-icon">
                <feather-icon icon="BarChart2Icon" size="20" />
              </div>
              <div class="feature-text">
                <h4>Gestão Inteligente</h4>
                <p>Relatórios e análises para otimizar suas vendas</p>
              </div>
            </div>
          </div>
        </div>
      </b-col>

      <!-- Right Side - Login Form -->
      <b-col lg="5" class="d-flex align-items-center justify-content-center login-side">
        <div class="login-container">
          <!-- Mobile Logo -->
          <div class="mobile-logo d-lg-none">
            <img
              :src="brandLogo"
              :alt="brandName || 'SalesFlow'"
              class="brand-logo-mobile-img"
            />
          </div>

          <div class="login-header">
            <h2 class="login-title">Bem-vindo de volta</h2>
            <p class="login-subtitle">
              {{ brandName ? `Acesse o portal ${brandName}` : 'Acesse sua conta para continuar' }}
            </p>
          </div>

          <!-- Aviso: empresa não encontrada -->
          <b-alert v-if="brandError" variant="warning" show class="brand-error-alert">
            <feather-icon icon="AlertTriangleIcon" class="mr-1" />
            Empresa não encontrada. Verifique o endereço ou
            <b-link :to="{ name: 'tenant-select' }" class="alert-link">informe sua empresa</b-link>.
          </b-alert>

          <validation-observer ref="loginValidation">
            <b-form class="login-form" @submit.prevent>

              <div class="form-group-custom">
                <label class="form-label-custom" for="login-email">Email</label>
                <validation-provider #default="{ errors }" name="Email" rules="required|email">
                  <div class="input-wrapper" :class="{ 'has-error': errors.length > 0 }">
                    <feather-icon icon="MailIcon" class="input-icon" />
                    <b-form-input
                      id="login-email"
                      v-model="userEmail"
                      :state="errors.length > 0 ? false : null"
                      class="form-input-custom"
                      placeholder="seu@email.com"
                    />
                  </div>
                  <small class="error-text" v-if="errors[0]">{{ errors[0] }}</small>
                </validation-provider>
              </div>

              <div class="form-group-custom">
                <div class="label-row">
                  <label class="form-label-custom" for="login-password">Senha</label>
                  <b-link class="forgot-link" @click="esqueciSenha();">
                    Esqueceu a senha?
                  </b-link>
                </div>
                <validation-provider #default="{ errors }" name="Password" rules="required">
                  <div class="input-wrapper" :class="{ 'has-error': errors.length > 0 }">
                    <feather-icon icon="LockIcon" class="input-icon" />
                    <b-form-input
                      id="login-password"
                      v-model="password"
                      :state="errors.length > 0 ? false : null"
                      class="form-input-custom"
                      :type="passwordFieldType"
                      placeholder="Digite sua senha"
                    />
                    <feather-icon
                      class="toggle-password"
                      :icon="passwordToggleIcon"
                      @click="togglePasswordVisibility"
                    />
                  </div>
                  <small class="error-text" v-if="errors[0]">{{ errors[0] }}</small>
                </validation-provider>
              </div>

              <div class="form-group-custom remember-row">
                <b-form-checkbox id="remember-me" v-model="status" class="custom-checkbox">
                  Manter-me conectado
                </b-form-checkbox>
              </div>

              <b-button type="submit" variant="primary" class="login-button" block @click="validationForm">
                Entrar
                <feather-icon icon="ArrowRightIcon" class="ml-2" />
              </b-button>
            </b-form>
          </validation-observer>

          <div class="login-footer">
            <p class="register-text">
              Quer cadastrar um cliente?
              <b-link :to="{ name: 'apps-pre-cadastro-cliente' }" class="register-link">
                Pré-Cadastro aqui
              </b-link>
            </p>
          </div>

          <div class="powered-by">
            <img src="@/assets/images/logo-salesflow.png" alt="Gildan" class="footer-logo-img" />
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- Modal de seleção de perfil -->
    <b-modal ref="myModal" id="modal-default" ok-only ok-title="Entrar" @ok="logarPerfil()" centered
      title="Selecione seu perfil" modal-class="profile-modal">
      <b-card-text>
        <b-form-group label="Perfil de acesso" label-for="login-perfil">
          <b-form-select v-model="selected" :options="options" class="profile-select" />
        </b-form-group>
      </b-card-text>
    </b-modal>
  </div>
</template>

<script>
/* eslint-disable global-require */
import { useApi } from '@/composables/useApi';
import Store from '@/store';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import VuexyLogo from '@core/layouts/components/Logo.vue';
import {
  BRow, BCol, BLink, BFormGroup, BFormInput, BInputGroupAppend, BInputGroup, BFormCheckbox, BCardText, BCardTitle, BImg, BForm, BButton, BAlert,
} from 'bootstrap-vue';
import { applyTenantColor, applyTenantSecondaryColor, resetTenantColors } from '@/utils/tenantTheme';
import { required, email } from '@core/utils/validations/validations';
import { togglePasswordVisibility } from '@core/mixins/ui/forms';
import store from '@/store';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { getHomeRouteForLoggedInUser } from '@/auth/utils';
import Swal from 'sweetalert2';
import useJwt from '@/auth/jwt/useJwt';
import Notifier from '@/libs/Notifier';
import axios from '@/libs/axios';
import InitialDataService from '@/services/InitialData.service';

const apiBack = useApi();
const notify = new Notifier();

export default {
  components: {
    BRow,
    BCol,
    BLink,
    BFormGroup,
    BFormInput,
    BInputGroupAppend,
    BInputGroup,
    BFormCheckbox,
    BCardText,
    BCardTitle,
    BImg,
    BForm,
    BButton,
    BAlert,
    VuexyLogo,
    ValidationProvider,
    ValidationObserver,
  },
  mixins: [togglePasswordVisibility],
  data() {
    return {
      userData: JSON.parse(localStorage.getItem('userData')),
      password: '',
      userEmail: '',
      userDados: '',
      tenantSlug: this.$route.params.tenantSlug || localStorage.getItem('tenantSlug') || process.env.VUE_APP_TENANT_SLUG || 'dkp',
      // Branding do tenant (carregado da rota pública /tenants/public/:slug)
      brandLogo: require('@/assets/images/logo-salesflow.png'),
      brandName: '',
      hasCustomLogo: false,
      brandPrimary: '',
      brandError: false,
      imagemLogin: {},
      logoImg: require('../../../public/logo.webp'),
      sideImg: require('@/assets/images/login.webp'),
      status: false,
      selected: null,
      options: [],
      // validation rulesimport store from '@/store/index'
      required,
      email,
    };
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon';
    },
    imgUrl() {
      if (store.state.appConfig.layout.skin === 'dark') {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.sideImg = require('@/assets/images/login.webp');
        return this.sideImg;
      }
      return this.sideImg;
    },
  },
  watch: {
    status() {
      this.salvarPreferenciasLogin();
    },
  },

  mounted() {
    this.limparStorage();
    this.carregarBranding();
    if (localStorage.getItem('rememberMe') === 'true') {
      this.status = JSON.parse(localStorage.getItem('rememberMe'));
      this.userEmail = localStorage.getItem('email');
      this.password = localStorage.getItem('password');
    } else if (localStorage.getItem('rememberMe') === 'false') {
      this.status = JSON.parse(localStorage.getItem('rememberMe'));
      this.userEmail = '';
      this.password = '';
    }
    const { segmento } = this.$route.params;
    if (segmento) {
      console.log('Segmento capturado:', segmento);
      axios.get(`/imagem-login/${segmento}`).then((response) => {
        console.log(response);
        this.imagemLogin = response.data;
      }).catch((error) => {
        console.error('Erro ao fazer requisição:', error);
      });
    } else {
      this.imagemLogin.url = require('@/assets/images/login.webp');
    }
  },
  methods: {
    async carregarBranding() {
      const slug = (this.tenantSlug || '').toLowerCase().trim();
      if (!slug) {
        this.aplicarBrandingNeutro();
        this.brandError = true;
        return;
      }
      try {
        const { data } = await axios.get(`/tenants/public/${slug}`);
        this.brandName = data.nome || '';
        if (data.logo_url) {
          this.brandLogo = data.logo_url;
          this.hasCustomLogo = true;
        }
        if (data.cor_primaria) {
          this.brandPrimary = data.cor_primaria;
          applyTenantColor(data.cor_primaria);
        }
        if (data.cor_secundaria) {
          applyTenantSecondaryColor(data.cor_secundaria);
        }
        this.brandError = false;
      } catch (error) {
        // Slug inválido / empresa não encontrada → visual neutro + aviso
        this.aplicarBrandingNeutro();
        this.brandError = true;
        // Auto-cura: um slug inválido (ex.: "homacmillan") não pode persistir no
        // localStorage, senão é reenviado no header x-tenant-slug e re-preenche telas.
        if (error && error.response && error.response.status === 404
            && localStorage.getItem('tenantSlug') === slug) {
          localStorage.removeItem('tenantSlug');
        }
      }
    },
    aplicarBrandingNeutro() {
      this.brandName = '';
      this.brandLogo = require('@/assets/images/logo-salesflow.png');
      this.hasCustomLogo = false;
      this.brandPrimary = '';
      resetTenantColors();
    },
    async validationForm() {
      this.$refs.loginValidation.validate().then((success) => {
        if (success) {
          this.loginInput();
        }
      });
    },
    async limparStorage() {
      localStorage.removeItem(useJwt.jwtConfig.storageTokenKeyName);
      localStorage.removeItem(useJwt.jwtConfig.storageRefreshTokenKeyName);
      localStorage.removeItem('userData');
      localStorage.removeItem('userPermissions');
      localStorage.removeItem('perfilLogado');
      localStorage.removeItem('lgn_admin');
      localStorage.removeItem('lgn_admin_metadata');
      localStorage.removeItem('infoPedidoForm');
      localStorage.removeItem('clienteData');
      localStorage.removeItem('representanteData');
      // Nota: tenantSlug NÃO é removido — precisa persistir entre sessões
    },
    salvarPreferenciasLogin() {
      localStorage.setItem('rememberMe', this.status);
      if (this.status) {
        localStorage.setItem('email', this.userEmail);
        localStorage.setItem('password', this.password);
      } else {
        localStorage.removeItem('email');
        localStorage.removeItem('password');
      }
    },
    async loginInput() {
      this.salvarPreferenciasLogin();
      const slug = (this.tenantSlug || 'dkp').toLowerCase().trim();
      // Só persiste o slug se a empresa foi validada (branding OK). Se o usuário
      // chegou numa URL inválida (/:slug/login com brandError), não re-poluímos o
      // localStorage com um tenant inexistente.
      if (!this.brandError) {
        localStorage.setItem('tenantSlug', slug);
      }
      localStorage.removeItem('dkpImpersonateSlug');
      localStorage.removeItem('tenant_cached_slug');
      localStorage.removeItem('tenant_cor_primaria');
      localStorage.removeItem('tenant_cor_secundaria');
      localStorage.removeItem('tenant_logo_url');
      await this.$refs.loginValidation.validate().then((success) => {
        if (success) {
          useJwt.login({
            usuario_email: this.userEmail,
            usuario_senha: this.password,
          })
            .then(async (response) => {
              this.userDados = response.data;
              const { userData } = response.data;

              if (userData.status === 0) {
                Swal.fire(
                  'Usuário bloqueado!',
                  `<div style="text-align: left; flex-wrap: nowrap"><p>Favor entrar em contato com a equipe SalesFlow</p>
<p>E-mail: <strong>financeiro@dkpsales.com</strong></p></div>`,
                  'error',
                );
              } else if (userData.perfils.length === 1) {
                console.log(userData, 'USERDATAPERFIL1');
                Store.commit('auth/SET_USER_AUTH', {
                  nome: userData.nome,
                  email: userData.email,
                  status: userData.status,
                });
                useJwt.setToken(response.data.accessToken);
                useJwt.setRefreshToken(response.data.refreshToken);
                localStorage.setItem('perfilLogado', JSON.stringify(userData.perfils[0]));
                localStorage.setItem('userData', JSON.stringify(userData));
                const perfilPermissions = await this.addDelegatedApprovalPermission(
                  this.buildPermissionsForPerfil(userData.perfils[0]),
                );
                localStorage.setItem('userPermissions', JSON.stringify(perfilPermissions));
                this.$ability.update(perfilPermissions);

                // O perfil de cliente final pode se chamar 'Cliente' OU 'ClienteFinal' (varia por tenant).
                if (['Cliente', 'ClienteFinal'].includes(userData.perfils[0].perfil_acesso_nome)) {
                  localStorage.setItem('firstClickClienteFinal', 'true');
                } else {
                  localStorage.removeItem('firstClickClienteFinal');
                }

                this.gerenciarDadosInfoPedido(userData.id);

                Swal.fire({
                  title: 'Carregando dados iniciais',
                  html: '<div class="sf-loader-wrapper"><div class="sf-loader"></div><p class="sf-loader-text">Aguarde enquanto preparamos seu ambiente...</p></div>',
                  allowOutsideClick: false,
                  customClass: {
                    popup: 'sf-swal-loading'
                  },
                  showConfirmButton: false,
                  willOpen: () => {
                    Swal.showLoading();
                  },
                });

                await InitialDataService.loadInitialData();

                Swal.close();

                this.$store.commit('auth/SET_USER', this.userDados);
                this.$store.dispatch('tenantStore/carregarTenant').catch(() => {});
                this.$store.dispatch('preferencias/load', { force: true }).catch(() => {});
                this.$router.replace(getHomeRouteForLoggedInUser(userData.perfils[0].perfil_acesso_nome));
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: `Bem-vindo ${userData.nome}`,
                    icon: 'CoffeeIcon',
                    variant: 'success',

                  },
                },
                {
                  position: 'top-center',
                });
              } else if (userData.perfils.length > 1) {
                Store.commit('auth/SET_USER_AUTH', {
                  nome: userData.nome,
                  email: userData.email,
                  status: userData.status,
                });

                this.gerenciarDadosInfoPedido(userData.id);

                this.$refs.myModal.show();
                this.options = userData.perfils.map((perfil) => ({
                  text: perfil.perfil_acesso_nome,
                  value: perfil,
                }));
              }
            }).catch((error) => {
              notify.error('Email ou senha incorretos!');
            });
        }
      });
    },
    gerenciarDadosInfoPedido(userId) {
      try {
        const userPedidoKey = `infoPedidoForm_${userId || 'default'}`;
        const existingData = localStorage.getItem(userPedidoKey);

        if (existingData) {
          console.log('Dados antigos de InfoPedido encontrados. Reinicializando...');

          // Remover os dados antigos
          localStorage.removeItem(userPedidoKey);

          this.$store.dispatch('InfoPedido/resertInfoPedido');
        }
      } catch (error) {
        console.error('Erro ao gerenciar dados de InfoPedido:', error);
      }
    },
    async logarPerfil() {
      localStorage.setItem('perfilLogado', JSON.stringify(this.selected));
      this.$refs.myModal.hide();
      useJwt.setToken(this.userDados.accessToken);
      useJwt.setRefreshToken(this.userDados.refreshToken);
      localStorage.setItem('userData', JSON.stringify(this.userDados));
      const perfilPermissions = await this.addDelegatedApprovalPermission(
        this.buildPermissionsForPerfil(this.selected),
      );
      localStorage.setItem('userPermissions', JSON.stringify(perfilPermissions));
      this.$ability.update(perfilPermissions);

      // O perfil de cliente final pode se chamar 'Cliente' OU 'ClienteFinal' (varia por tenant).
      if (this.selected && ['Cliente', 'ClienteFinal'].includes(this.selected.perfil_acesso_nome)) {
        localStorage.setItem('firstClickClienteFinal', 'true');
      } else {
        localStorage.removeItem('firstClickClienteFinal');
      }

      Swal.fire({
        title: 'Preparando seu perfil',
        html: '<div class="sf-loader-wrapper"><div class="sf-loader"></div><p class="sf-loader-text">Aguarde enquanto sincronizamos seus dados...</p></div>',
        allowOutsideClick: false,
        customClass: {
          popup: 'sf-swal-loading'
        },
        showConfirmButton: false,
        willOpen: () => {
          Swal.showLoading();
        },
      });

      await InitialDataService.loadInitialData();

      Swal.close();

      this.$store.commit('auth/SET_USER', this.userDados);
      this.$store.dispatch('tenantStore/carregarTenant').catch(() => {});
      this.$store.dispatch('preferencias/load', { force: true }).catch(() => {});
      this.$router.replace(getHomeRouteForLoggedInUser(this.selected.perfil_acesso_nome));

      this.$toast({
        component: ToastificationContent,
        props: {
          title: `Bem-vindo ${this.userDados.userData.nome}`,
          icon: 'CoffeeIcon',
          variant: 'success',

        },
      },
      {
        position: 'top-center',
      });
    },
    async esqueciSenha() {
      if (!this.userEmail) {
        notify.error('Preencha o campo email!');
        return;
      }

      Swal.fire({
        title: 'Buscando conta',
        html: '<div class="sf-loader-wrapper"><div class="sf-loader"></div><p class="sf-loader-text">Aguarde...</p></div>',
        allowOutsideClick: false,
        customClass: {
          popup: 'sf-swal-loading'
        },
        showConfirmButton: false,
        willOpen: () => {
          Swal.showLoading();
        },
      });

      localStorage.setItem('verifyEmail', this.userEmail);

      await axios.post('/seguranca/esqueciSenha', {
        usuario_email: this.userEmail,
      })
        .then((res) => {
          if (res.data.message === 'E-mail inválido') {
            notify.error(res.data.message);
          } else {
            notify.success('Email de redefinição enviado com sucesso!');
            this.$router.push({ name: 'forgot-password' });
          }
        })
        .catch((error) => {
          console.log('error', error);
          notify.error('Erro ao processar a solicitação. Tente novamente.');
        })
        .finally(() => {
          Swal.close();
        });
    },
    buildPermissionsForPerfil(perfil) {
      const permissions = Array.isArray(perfil?.Permissoes) ? [...perfil.Permissoes] : [];
      if (['AdminHanes', 'Administrador'].includes(perfil?.perfil_acesso_nome)) {
        const hasAdminHanes = permissions.some((perm) => perm?.action === 'read' && perm?.subject === 'AdminHanes');
        if (!hasAdminHanes) {
          permissions.push({ action: 'read', subject: 'AdminHanes' });
        }
      }
      if (['Gerente', 'Supervisor'].includes(perfil?.perfil_acesso_nome)) {
        const hasCatalogoProdutos = permissions.some(
          (perm) => perm?.action === 'visualizar' && perm?.subject === 'CatalogoProdutos',
        );
        if (!hasCatalogoProdutos) {
          permissions.push({ action: 'visualizar', subject: 'CatalogoProdutos' });
        }
      }
      // GESTÃO do Budget (definir valor total + período) é do perfil Diretor.
      // Diretor recebe Budget:manage (admins já têm via subject 'all').
      // Também recebe alocar:BudgetAlocacao para abrir a tela de Alocação — o
      // backend (_assertPodeAlocar) já autoriza Diretor; manage:Budget no CASL
      // NÃO satisfaz alocar:BudgetAlocacao, então o grant precisa ser explícito.
      if (perfil?.perfil_acesso_nome === 'Diretor') {
        const hasBudgetManage = permissions.some(
          (perm) => perm?.action === 'manage' && perm?.subject === 'Budget',
        );
        if (!hasBudgetManage) {
          permissions.push({ action: 'manage', subject: 'Budget' });
        }
        const hasBudgetAlocacao = permissions.some(
          (perm) => perm?.action === 'alocar' && perm?.subject === 'BudgetAlocacao',
        );
        if (!hasBudgetAlocacao) {
          permissions.push({ action: 'alocar', subject: 'BudgetAlocacao' });
        }
      }
      // Supervisores NÃO definem mais total/período — recebem a tela de Alocação
      // (dividir verba entre representantes + transferências). Usamos a action
      // 'alocar' (não 'read') para que o read:all do Representante NÃO conceda.
      if (perfil?.perfil_acesso_nome === 'Supervisor') {
        // Defensivo: remove qualquer manage:Budget herdado de grant antigo no
        // banco — o Supervisor não pode mais definir valor total/período.
        for (let i = permissions.length - 1; i >= 0; i -= 1) {
          if (permissions[i]?.action === 'manage' && permissions[i]?.subject === 'Budget') {
            permissions.splice(i, 1);
          }
        }
        const hasBudgetAlocacao = permissions.some(
          (perm) => perm?.action === 'alocar' && perm?.subject === 'BudgetAlocacao',
        );
        if (!hasBudgetAlocacao) {
          permissions.push({ action: 'alocar', subject: 'BudgetAlocacao' });
        }
      }
      // O Dashboard de Budget é uma consulta pessoal (verba + pedidos que deram
      // baixa). Liberado para Diretor, Supervisor e Representante; admins já têm via 'all'.
      if (['Diretor', 'Supervisor', 'Representante'].includes(perfil?.perfil_acesso_nome)) {
        const hasBudgetDashboard = permissions.some(
          (perm) => perm?.action === 'read' && perm?.subject === 'BudgetDashboard',
        );
        if (!hasBudgetDashboard) {
          permissions.push({ action: 'read', subject: 'BudgetDashboard' });
        }
      }
      return permissions;
    },
    async addDelegatedApprovalPermission(permissions) {
      const result = Array.isArray(permissions) ? [...permissions] : [];
      if (result.some((perm) => perm?.action === 'read' && perm?.subject === 'PedidosAprovacao')) {
        return result;
      }

      try {
        // A delegação é uma capacidade temporária do usuário autenticado, não
        // uma alteração permanente da matriz do perfil. O backend continua
        // sendo a autoridade final e revalida a vigência em toda chamada.
        const response = await axios.get('/budget/delegacao/status');
        if (response?.data?.data?.podeAcessarAprovacoesComoDelegado) {
          result.push({ action: 'read', subject: 'PedidosAprovacao' });
        }
      } catch (error) {
        // Falhar ao consultar uma delegação opcional não deve impedir o login.
      }
      return result;
    },
  },
};
</script>

<style scoped lang="scss">
/* 
  B2B Premium Palette (Login Screen)
  Deep Navy: #01094e
  Ocean Blue: #0575b5
  Vibrant Cyan: #00b7d6
  Pale Background: #f8fafc
  Light Border: #e2e8f0
*/

.login-page {
  min-height: 100vh;
  background: #ffffff;
  /* Removed display: flex; so the Bootstrap grid (b-row) can take full width naturally */
}

/* ---------------- LEFT SIDE (BRANDING) ---------------- */
.branding-side {
  background: #01094e;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4rem;

  /* Subtle geometric/gradient overlay */
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle at center, rgba(5, 117, 181, 0.15) 0%, transparent 50%),
                radial-gradient(circle at bottom right, rgba(0, 183, 214, 0.1) 0%, transparent 40%);
    z-index: 0;
  }
}

.branding-content {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 650px; /* Increased from 540px to use more space */
  display: flex;
  flex-direction: column;
}

.branding-header {
  text-align: center;
  margin-bottom: 3rem;
}

.brand-logo-img {
  width: 280px; /* Larger */
  height: auto;
  filter: brightness(0) invert(1); /* Pure white on navy */
}

/* Logo customizado do tenant: não força branco (preserva cores originais) */
.brand-logo-img.brand-logo-custom {
  filter: none;
  max-width: 280px;
  max-height: 120px;
  object-fit: contain;
  background: rgba(255, 255, 255, 0.92);
  padding: 0.75rem 1rem;
  border-radius: 12px;
}

.branding-side .brand-name {
  margin-top: 1.25rem;
  color: #ffffff !important;
  font-weight: 700;
  font-size: 1.5rem;
  letter-spacing: 0.5px;
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.25);
}

.brand-error-alert {
  font-size: 0.85rem;
  margin-bottom: 1.25rem;
}

.banner-showcase {
  margin: 1rem 0 3rem;
  display: flex;
  justify-content: center;
}

.banner-image {
  width: 100%; /* Take full width of the large container */
  max-height: 38vh; /* Taller image */
  object-fit: contain;
  border-radius: 12px;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.6);
}

.branding-features {
  display: flex;
  flex-direction: column;
  gap: 1.25rem; /* More spacing */
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1.5rem; /* Larger padding */
  background: rgba(255, 255, 255, 0.08); 
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px; /* Bigger rounding */
  transition: transform 0.2s ease, background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    transform: translateX(4px);
  }
}

.feature-icon {
  width: 56px; /* Larger icon box */
  height: 56px;
  background: rgba(0, 183, 214, 0.2);
  border: 1px solid rgba(0, 183, 214, 0.5);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #00b7d6; 
  flex-shrink: 0;
  
  /* Make embedded feather-icons larger */
  svg {
    width: 28px;
    height: 28px;
  }
}

.feature-text {
  h4 {
    color: #ffffff !important;
    font-size: 1.2rem; /* Larger title */
    font-weight: 600;
    margin-bottom: 0.35rem;
  }

  p {
    color: #d6f4fa !important;
    font-size: 0.95rem; /* Better readability */
    font-weight: 400;
    margin: 0;
    line-height: 1.5;
    opacity: 0.85;
  }
}

/* ---------------- RIGHT SIDE (LOGIN FORM) ---------------- */
.login-side {
  background: #ffffff;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4rem; /* More breathing room */
}

.login-container {
  width: 100%;
  max-width: 480px; /* Increased from 400px so forms feel robust */
  margin: 0 auto;
}

.mobile-logo {
  text-align: center;
  margin-bottom: 3rem;
}

.brand-logo-mobile-img {
  width: 240px; /* Larger */
  height: auto;
}

.login-header {
  margin-bottom: 3rem;
  text-align: left;
}

.login-title {
  font-size: 2.25rem; /* Extremely bold title */
  font-weight: 800;
  color: #01094e;
  margin-bottom: 0.75rem;
  letter-spacing: -0.02em;
}

.login-subtitle {
  color: #64748b; 
  font-size: 1.15rem; /* Larger */
  font-weight: 400;
  margin: 0;
}

.login-form {
  margin-bottom: 2rem;
}

.form-group-custom {
  margin-bottom: 1.75rem;
}

.form-label-custom {
  display: block;
  font-size: 0.95rem; /* Robust label */
  font-weight: 600;
  color: #334155;
  margin-bottom: 0.65rem;
}

.label-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.65rem;
}

.forgot-link {
  font-size: 0.9rem;
  color: #0575b5;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    color: #01094e;
    text-decoration: underline;
  }
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background: #ffffff;
  border: 1.5px solid #cbd5e1; /* Thicker professional light gray */
  border-radius: 10px; /* Rounded 10px instead of 8px */
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus-within {
    border-color: #0575b5;
    box-shadow: 0 0 0 4px rgba(5, 117, 181, 0.15);
  }

  &.has-error {
    border-color: #ef4444;
    &:focus-within {
      box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.15);
    }
  }
}

.input-icon {
  position: absolute;
  left: 1.25rem; /* Centered visually with taller input */
  color: #64748b;
  width: 20px;
  height: 20px;
  pointer-events: none;
}

.form-input-custom {
  border: none !important;
  background: transparent !important;
  padding: 1rem 1.25rem 1rem 3.5rem !important; /* Larger padding */
  font-size: 1.05rem; /* Huge readable font */
  color: #0f172a !important;
  font-weight: 500;
  width: 100%;
  height: 56px; /* High imposing inputs */

  &:focus {
    box-shadow: none !important;
  }

  &::placeholder {
    color: #94a3b8;
    font-weight: 400;
  }
}

.toggle-password {
  position: absolute;
  right: 1.25rem;
  color: #64748b;
  width: 20px;
  height: 20px;
  cursor: pointer;
  
  &:hover {
    color: #0575b5;
  }
}

.error-text {
  color: #ef4444;
  font-size: 0.85rem;
  font-weight: 500;
  margin-top: 0.5rem;
  display: block;
}

.remember-row {
  margin-top: 1.25rem;
}

.custom-checkbox {
  font-size: 0.95rem; /* Larger text */
  font-weight: 500;
  color: #475569;
}

/* Solid Professional Button */
.login-button {
  background: #0575b5 !important;
  border: none !important;
  padding: 1.1rem 2rem; /* Substantial clickable area */
  font-size: 1.15rem; /* Commanding font */
  font-weight: 600;
  border-radius: 10px;
  margin-top: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff !important;
  transition: all 0.2s ease;
  box-shadow: 0 4px 6px -1px rgba(5, 117, 181, 0.2), 0 2px 4px -1px rgba(5, 117, 181, 0.1);

  svg {
    width: 22px;
    height: 22px;
  }

  &:hover {
    background: #04639a !important;
    transform: translateY(-2px);
    box-shadow: 0 10px 15px -3px rgba(5, 117, 181, 0.3), 0 4px 6px -2px rgba(5, 117, 181, 0.15);
  }

  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
}

.login-footer {
  text-align: center;
  margin-top: 2.5rem;
  padding-top: 2rem;
}

.register-text {
  color: #475569;
  font-size: 1rem;
  font-weight: 400;
  margin: 0;
}

.register-link {
  color: #0575b5;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    color: #01094e;
    text-decoration: underline;
  }
}

.powered-by {
  text-align: center;
  margin-top: 3rem;
}

.footer-logo-img {
  width: 130px; /* More legible */
  height: auto;
  opacity: 0.8;
  filter: grayscale(100%);
  transition: filter 0.3s ease, opacity 0.3s ease;

  &:hover {
    opacity: 1;
    filter: grayscale(0%);
  }
}

/* ================= Dark Layout Extra Context ================= */
body.dark-layout .login-page {
  background: #0f172a;
}
body.dark-layout .login-side {
  background: #0f172a;
}
body.dark-layout .login-title {
  color: #f8fafc;
}
body.dark-layout .form-label-custom {
  color: #cbd5e1;
}
body.dark-layout .login-subtitle,
body.dark-layout .register-text,
body.dark-layout .custom-checkbox {
  color: #94a3b8;
}
body.dark-layout .input-wrapper {
  background: #1e293b;
  border-color: #334155;
  
  &:focus-within {
    border-color: #00b7d6;
    box-shadow: 0 0 0 3px rgba(0, 183, 214, 0.2);
  }
}
body.dark-layout .form-input-custom {
  color: #f8fafc !important;
  &::placeholder {
    color: #475569;
  }
}
body.dark-layout .login-button {
  background: #0575b5 !important;
}

/* ================= Profile Modal Customization ================= */
.profile-modal {
  .modal-content {
    background: #ffffff;
    border: none;
    border-radius: 12px;
    box-shadow: 0 15px 50px rgba(0, 9, 78, 0.2);
    overflow: hidden;
  }
  
  .modal-header {
    background: linear-gradient(135deg, #01094e 0%, #031b73 100%);
    border-bottom: none;
    padding: 1.5rem 2rem;
    
    .modal-title {
      color: #ffffff;
      font-weight: 700;
      font-size: 1.25rem;
    }

    .close {
      color: #ffffff;
      opacity: 0.8;
      text-shadow: none;
      transition: opacity 0.2s;
      
      &:hover {
        opacity: 1;
      }
    }
  }

  .modal-body {
    padding: 2rem;
    
    .form-group {
      margin-bottom: 0;
    }

    label {
      font-size: 0.95rem;
      font-weight: 600;
      color: #334155;
      margin-bottom: 0.75rem;
    }
  }

  .profile-select {
    width: 100%;
    height: 50px;
    padding: 0.5rem 1rem;
    font-size: 1.05rem;
    color: #0f172a;
    background-color: #f8fafc;
    border: 1.5px solid #cbd5e1;
    border-radius: 8px;
    appearance: none;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23334155' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right 1rem center;
    background-size: 16px 12px;
    transition: all 0.2s ease;

    &:focus {
      outline: none;
      border-color: #0575b5;
      box-shadow: 0 0 0 4px rgba(5, 117, 181, 0.15);
      background-color: #ffffff;
    }
  }

  .modal-footer {
    border-top: 1px solid #e2e8f0;
    padding: 1.25rem 2rem;
    background: #f8fafc;
    
    .btn-primary {
      background: #0575b5 !important;
      border: none !important;
      padding: 0.75rem 1.5rem;
      font-size: 1rem;
      font-weight: 600;
      border-radius: 8px;
      transition: all 0.2s ease;
      box-shadow: 0 4px 6px -1px rgba(5, 117, 181, 0.2);

      &:hover {
        background: #04639a !important;
        transform: translateY(-1px);
        box-shadow: 0 8px 12px -3px rgba(5, 117, 181, 0.3);
      }
    }
  }
}

body.dark-layout .profile-modal {
  .modal-content {
    background: #0f172a;
    border: 1px solid #1e293b;
  }
  
  .modal-header {
    background: #000424;
    border-bottom: 1px solid #1e293b;
  }
  
  .modal-body label {
    color: #cbd5e1;
  }
  
  .profile-select {
    background-color: #1e293b;
    border-color: #334155;
    color: #f8fafc;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23cbd5e1' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e");
    
    &:focus {
      background-color: #0f172a;
      border-color: #00b7d6;
      box-shadow: 0 0 0 3px rgba(0, 183, 214, 0.2);
    }
  }
  
  .modal-footer {
    background: #0f172a;
    border-color: #1e293b;
  }
}

/* ================= Responsiveness ================= */
@media (max-width: 1200px) {
  .branding-side, .login-side {
    padding: 3rem 2rem;
  }
  .branding-content {
    max-width: 550px;
  }
}

@media (max-width: 992px) {
  .login-side {
    padding: 3rem 1.5rem;
  }
  .login-container {
    max-width: 100%;
  }
}

@media (max-width: 576px) {
  .login-side {
    padding: 2rem 1rem;
  }
  .login-title {
    font-size: 1.75rem;
  }
  .brand-logo-mobile-img {
    width: 200px;
  }
  .form-input-custom {
    height: 50px;
    font-size: 1rem;
  }
  .login-button {
    padding: 1rem 1.5rem;
    font-size: 1.05rem;
  }
}

@media (max-height: 800px) {
  .branding-side, .login-side {
    padding: 2.5rem;
  }
  .banner-showcase {
    margin: 1.5rem 0 2rem;
  }
  .banner-image {
    max-height: 25vh;
  }
  .branding-features {
    gap: 1rem;
  }
  .feature-item {
    padding: 1rem;
  }
  .login-header {
    margin-bottom: 2rem;
  }
  .form-input-custom {
    height: 50px;
  }
  .login-footer {
    margin-top: 1.25rem;
    padding-top: 1.25rem;
  }
  .powered-by {
    margin-top: 1.5rem;
  }
}

/* ================= SweetAlert Loading Customization ================= */
.sf-swal-loading {
  background: #ffffff !important;
  border-radius: 16px !important;
  box-shadow: 0 20px 60px rgba(0, 9, 78, 0.15) !important;
  padding: 3rem 2rem !important;
  width: auto !important;
  min-width: 380px !important;

  .swal2-title {
    color: #01094e !important;
    font-size: 1.5rem !important;
    font-weight: 800 !important;
    margin-bottom: 2rem !important;
  }

  .swal2-html-container {
    margin: 0 !important;
    overflow: hidden !important;
  }
}

.sf-loader-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
}

.sf-loader {
  width: 50px;
  height: 50px;
  border: 4px solid #f8fafc;
  border-top-color: #00b7d6; /* Vibrant Cyan */
  border-right-color: #0575b5; /* Ocean Blue */
  border-radius: 50%;
  animation: sf-spin 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
}

@keyframes sf-spin {
  to { transform: rotate(360deg); }
}

.sf-loader-text {
  color: #64748b;
  font-size: 1.05rem;
  font-weight: 500;
  margin: 0;
}

body.dark-layout .sf-swal-loading {
  background: #0f172a !important;
  border: 1px solid #1e293b !important;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5) !important;

  .swal2-title {
    color: #f8fafc !important;
  }
}

body.dark-layout .sf-swal-loading .sf-loader {
  border: 4px solid #1e293b;
  border-top-color: #00b7d6;
  border-right-color: #0575b5;
}

body.dark-layout .sf-swal-loading .sf-loader-text {
  color: #94a3b8;
}
</style>
